import { Component, input, output, ContentChild, TemplateRef, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Pagination } from '../pagination/pagination';

export interface TableColumn {
  key: string;
  label: string;
  align?: 'left' | 'center' | 'right';
  sortable?: boolean;
  width?: string;
}

@Component({
  selector: 'app-data-table',
  imports: [CommonModule, Pagination],
  templateUrl: './data-table.html',
})
export class DataTable<T = any> {
  title = input.required<string>();
  columns = input.required<TableColumn[]>();
  data = input.required<T[]>();
  trackByKey = input.required<string>();
  selectedItem = input<T | null>(null);
  selectedKey = input<string>('');
  currentPage = input.required<number>();
  totalPages = input.required<number>();
  totalItems = input.required<number>();
  pageSize = input.required<number>();
  itemLabel = input<string>('items');
  showDelete = input<boolean>(true);

  rowClick = output<T>();
  deleteClick = output<T>();
  pageChange = output<number>();

  @ContentChild('cellTemplate') cellTemplate?: TemplateRef<any>;

  sortColumn = signal<string>('');
  sortDirection = signal<'asc' | 'desc'>('asc');

  sortedData = computed(() => {
    const col = this.sortColumn();
    const dir = this.sortDirection();
    const rows = [...this.data()];
    if (!col) return rows;
    return rows.sort((a, b) => {
      const aVal = this.getCellValue(a, col);
      const bVal = this.getCellValue(b, col);
      const aStr = aVal == null ? '' : String(aVal).toLowerCase();
      const bStr = bVal == null ? '' : String(bVal).toLowerCase();
      if (aStr < bStr) return dir === 'asc' ? -1 : 1;
      if (aStr > bStr) return dir === 'asc' ? 1 : -1;
      return 0;
    });
  });

  sortBy(key: string): void {
    if (this.sortColumn() === key) {
      this.sortDirection.update(d => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      this.sortColumn.set(key);
      this.sortDirection.set('asc');
    }
  }

  getCellValue(item: any, key: string): any {
    const keys = key.split('.');
    let value = item;
    for (const k of keys) {
      value = value?.[k];
    }
    return value ?? '';
  }

  isSelected(item: any): boolean {
    const key = this.selectedKey();
    if (!key || !this.selectedItem()) return false;
    return item[key] === (this.selectedItem() as any)?.[key];
  }

  onDelete(item: T, event: Event): void {
    event.stopPropagation();
    this.deleteClick.emit(item);
  }
}
